/**
 * Centralized, Zod-validated environment variable accessor.
 *
 * ALL access to import.meta.env should go through this module.
 * Never read import.meta.env directly in application code.
 *
 * In development: throws on invalid/missing required variables.
 * In production:  logs a warning and falls back to schema defaults.
 */

import { z } from 'zod';

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().min(1),
  VITE_APP_URL: z.string().url(),
  VITE_SENTRY_DSN: z.string().default(''),
  VITE_SENTRY_ENVIRONMENT: z.string().default('development'),
  VITE_SENTRY_RELEASE: z.string().default(''),
  VITE_POSTHOG_KEY: z.string().default(''),
  VITE_POSTHOG_HOST: z.string().default('https://us.i.posthog.com'),
  VITE_ENABLE_DEV_TOOLS: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  VITE_EVENTS_SSO_ENABLED: z
    .string()
    .default('true')
    .transform((v) => v === 'true'),
  // TikTok connect is hidden behind a flag while M1 lands without the wizard
  // side of the integration. Flip to 'true' to surface TikTok in the
  // connect-account picker.
  VITE_ENABLE_TIKTOK_CONNECT: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  // Stripe publishable key (pk_test_/pk_live_). The isolated campaign checkout
  // page is only available when it is set.
  VITE_STRIPE_PUBLISHABLE_KEY: z.string().default(''),
});

export type Env = z.infer<typeof envSchema>;

function validateEnv(): Env {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    if (import.meta.env.DEV) {
      throw new Error(
        `Invalid environment variables:\n${JSON.stringify(result.error.format(), null, 2)}`,
      );
    }
    console.warn('Environment validation warnings:', result.error.format());
    // Fallback: parse again letting Zod apply defaults for optional fields.
    return envSchema
      .extend({
        VITE_API_BASE_URL: z.string().default(''),
        VITE_APP_URL: z.string().default(''),
      })
      .parse(import.meta.env) as Env;
  }

  return result.data;
}

export const env = validateEnv();
